'use client'

import { memo, useMemo } from 'react'
import { useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'

export type MarketingMarqueeProps = {
  /** Words / short phrases that scroll */
  items: readonly string[]
  /** Second row scrolls the opposite way (Webflow /pay pattern) */
  dual?: boolean
  className?: string
  /** Accessible label for the ticker region */
  label?: string
}

const MarqueeTrack = memo(function MarqueeTrack({
  items,
  reverse = false,
  reduced,
}: {
  items: readonly string[]
  reverse?: boolean
  reduced: boolean
}) {
  // baby: Duplicate once so CSS translate(-50%) loops seamlessly
  const loopItems = useMemo(
    () => (reduced ? [...items] : [...items, ...items]),
    [items, reduced],
  )

  return (
    <div className="ninxl-marquee-mask relative overflow-hidden py-2">
      <div
        className={cn(
          'flex w-max items-center gap-6 md:gap-10',
          reduced
            ? 'flex-wrap justify-center'
            : reverse
              ? 'ninxl-marquee-reverse'
              : 'ninxl-marquee-forward',
        )}
      >
        {loopItems.map((item, index) => (
          <span
            key={`${item}-${index}`}
            className="ninxl-8bit-heading shrink-0 text-sm tracking-[0.18em] text-[color:var(--ninxl-8bit-muted)] md:text-base"
          >
            {item}
            <span className="ml-6 text-[color:var(--ninxl-brand-teal)] md:ml-10" aria-hidden>
              ◆
            </span>
          </span>
        ))}
      </div>
    </div>
  )
})

/**
 * baby: Horizontal text ticker from Webflow /pay: infinite CSS marquee,
 * 8-bit type, dual optional reverse row.
 */
export const MarketingMarquee = memo(function MarketingMarquee({
  items,
  dual = true,
  className,
  label = 'Highlights',
}: MarketingMarqueeProps) {
  const reduced = Boolean(useReducedMotion())

  if (items.length === 0) {
    return null
  }

  return (
    <section
      className={cn('overflow-hidden py-4', className)}
      aria-label={label}
      data-section="marquee"
    >
      <MarqueeTrack items={items} reduced={reduced} />
      {dual ? <MarqueeTrack items={items} reverse reduced={reduced} /> : null}
    </section>
  )
})
